import { FieldDisplayType } from "./types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import {
  Mail,
  Link,
  USD,
  People,
  Organization,
  CalendarCheck,
  Frame,
  Numeric,
  MultiCheck,
  Checkbox,
  Tasks,
  Toggle,
  Calendar,
  TextArea,
  SingleLineText,
  Diamond,
  Magnet,
  Percentage,
  Product,
  ActionButton,
  Partners,
  Attachment,
  Phone,
  World
} from "@dreamhub/designsystem/icons";

export const FIELD_TYPE_ICONS = {
  [FieldDisplayType.TEXT]: SingleLineText,
  [FieldDisplayType.TEXTAREA]: TextArea,
  [FieldDisplayType.NUMBER]: Numeric,
  [FieldDisplayType.PERCENTAGE]: Percentage,
  [FieldDisplayType.DATE]: CalendarCheck,
  [FieldDisplayType.DATE_RANGE]: Calendar,
  [FieldDisplayType.SINGLE_SELECT]: Frame,
  [FieldDisplayType.MULTI_SELECT]: MultiCheck,
  [FieldDisplayType.EMAIL]: Mail,
  [FieldDisplayType.URL]: Link,
  [FieldDisplayType.CURRENCY]: USD,
  [FieldDisplayType.CURRENCIES]: USD,
  [FieldDisplayType.SWITCH]: Toggle,
  [FieldDisplayType.CHECKBOX]: Checkbox,
  [FieldDisplayType.CHECKLIST_ITEM]: Tasks,
  [FieldDisplayType.CHECKBOX_WITH_REQUIRED_TEXT]: Checkbox,
  [FieldDisplayType.USERS]: People,
  [FieldDisplayType.COMPANIES]: Organization,
  [FieldDisplayType.PEOPLE]: People,
  [FieldDisplayType.LEADS]: Magnet,
  [FieldDisplayType.DEALS]: Diamond,
  [FieldDisplayType.PARTNERS]: Partners,
  [FieldDisplayType.PARTNER_CONTACTS]: People,
  [FieldDisplayType.PRODUCTS]: Product,
  [FieldDisplayType.LICENSES]: Product,
  [FieldDisplayType.PEOPLE_IN_COMPANY]: People,
  [FieldDisplayType.BOOL]: Toggle,
  [FieldDisplayType.BUTTON]: ActionButton,
  [FieldDisplayType.FILE_UPLOAD]: Attachment,
  [FieldDisplayType.PHONE]: Phone,
  [FieldDisplayType.COUNTRIES]: World
} as const;

export const ADD_CUSTOM_FIELD_TRANSLATION_KEY =
  "settings.deals.fieldManagement.customField";

export const ADD_LINKED_FIELD_TRANSLATION_KEY =
  "settings.deals.fieldManagement.linkedField";

export const EDIT_CUSTOM_FIELD_TRANSLATION_KEY =
  "settings.deals.fieldManagement.editField";

export const RESTRICTED_FIELD_TYPES: FieldDisplayType[] = [
  FieldDisplayType.USERS,
  FieldDisplayType.COMPANIES,
  FieldDisplayType.PARTNERS,
  FieldDisplayType.PARTNER_CONTACTS,
  FieldDisplayType.LEADS,
  FieldDisplayType.DEALS,
  FieldDisplayType.PRODUCTS,
  FieldDisplayType.LICENSES,
  FieldDisplayType.BOOL,
  FieldDisplayType.CHECKBOX_WITH_REQUIRED_TEXT,
  FieldDisplayType.PEOPLE_IN_COMPANY,
  FieldDisplayType.BUTTON,
  FieldDisplayType.CURRENCIES,
  // Phone/Countries are the native People/Company column types this PR adds
  // (dial-code dropdown, closed ISO list) — not general-purpose custom-field
  // types. Without this, generateFieldDisplayTypeOptions offers them in the
  // Add-Custom-Field UI, but the generic creation/detail dispatchers
  // (formFields.tsx, detailFields.tsx) have no case for either and silently
  // fall back to plain text, defeating the closed-list guarantee for Country.
  FieldDisplayType.PHONE,
  FieldDisplayType.COUNTRIES
];

// Entity type options for the Linked Object dropdown
export const ENTITY_TYPE_OPTIONS = [
  {
    id: EntityType.COMPANY,
    displayName: "Company",
    icon: Organization
  },
  {
    id: EntityType.PERSON,
    displayName: "Person",
    icon: People
  },
  {
    id: EntityType.DEAL,
    displayName: "Deal",
    icon: Diamond
  },
  {
    id: EntityType.LEAD,
    displayName: "Lead",
    icon: Magnet
  }
] as const;

// Entity type mapping for linked field creation
export const ENTITY_TYPE_MAPPING: Partial<Record<EntityType, { tableName: string; dataSource: string; sourceKey: string }>> = {
  [EntityType.COMPANY]: {
    tableName: "companies",
    dataSource: "company",
    sourceKey: "company_id"
  },
  [EntityType.PERSON]: {
    tableName: "people",
    dataSource: "person",
    sourceKey: "contact_id"
  },
  [EntityType.LEAD]: {
    tableName: "leads",
    dataSource: "lead",
    sourceKey: "lead_id"
  },
  [EntityType.DEAL]: {
    tableName: "deals",
    dataSource: "deal",
    sourceKey: "deal_id"
  },
  [EntityType.USER]: {
    tableName: "users",
    dataSource: "user",
    sourceKey: "user_id"
  }
};

export const SUB_INDUSTRY_FIELD_KEY = "subIndustryId";
export const INDUSTRY_FIELD_KEY = "industryId";
export const PEOPLE_PHONE_FIELD_KEY = "phone";
export const COMPANY_COUNTRY_FIELD_KEY = "country";

// editable_system fields whose "required" toggle is user-configurable, as opposed
// to editable_system fields (e.g. Industry) that are always required by product design.
export const TOGGLEABLE_REQUIRED_EDITABLE_SYSTEM_FIELD_KEYS: string[] = [
  SUB_INDUSTRY_FIELD_KEY,
  PEOPLE_PHONE_FIELD_KEY,
  COMPANY_COUNTRY_FIELD_KEY
];

// Form configuration constants
export const FORM_DEFAULTS = {
  INITIAL_FORM_DATA: {},
  INITIAL_FORM_VALID: false,
  INITIAL_SUBMITTING: false
} as const;
